// AdminParams — Paramètres : profil du cabinet (nom, présentation, adresse, horaires, domaines) (v5.21.0).
// AdminDocuments — Documents reçus des clients. AdminReminders — Rappels WhatsApp (prototype).
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { T } from "./cabinetTheme";
import { getFounderToken } from "@/lib/founderApi";

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${getFounderToken()}` });
const field = { padding: "9px 12px", borderRadius: 9, border: "1px solid #E5E7EB", fontSize: 12.5, width: "100%", boxSizing: "border-box", fontFamily: "inherit" };

export const AdminParams = ({ slug }) => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/v3/cabinet/${slug}`).then((r) => {
      const c = r.data.cabinet;
      setF({ name: c.name, tagline: c.tagline || "", presentation: c.presentation || "", address: c.address || "", phone: c.phone || "", hours: c.hours || "", domains: (c.domains || []).join(", ") });
    });
  }, [slug]);
  if (!f) return <div style={T.sub}>Chargement…</div>;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/v3/cabinet/${slug}/admin/profile`, f, { headers: hdr() });
      toast.success("Profil du cabinet mis à jour ✓");
    } catch { toast.error("Enregistrement impossible"); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ ...T.card, display: "grid", gap: 9, maxWidth: 560 }} data-testid="admin-params">
      <h2 style={T.h2}>Profil du cabinet</h2>
      {[["name", "Nom du cabinet"], ["tagline", "Sous-titre"], ["address", "Adresse"], ["phone", "Téléphone"], ["hours", "Horaires"], ["domains", "Domaines (séparés par des virgules)"]].map(([k, l]) => (
        <label key={k} style={{ display: "grid", gap: 4 }}>
          <span style={{ ...T.sub, fontSize: 11, fontWeight: 800 }}>{l}</span>
          <input style={field} value={f[k]} onChange={set(k)} data-testid={`params-${k}`} />
        </label>
      ))}
      <label style={{ display: "grid", gap: 4 }}>
        <span style={{ ...T.sub, fontSize: 11, fontWeight: 800 }}>Présentation</span>
        <textarea style={{ ...field, minHeight: 80, resize: "vertical" }} value={f.presentation} onChange={set("presentation")} data-testid="params-presentation" />
      </label>
      <button style={{ ...T.btnGreen, width: "fit-content", opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save} data-testid="params-save">
        {saving ? "Enregistrement…" : "Enregistrer le profil"}
      </button>
    </div>
  );
};

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);

export const AdminDocuments = ({ slug }) => {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/v3/cabinet/${slug}/admin/documents`, { headers: hdr() })
      .then((r) => setDocs(r.data.documents)).catch(() => setDocs([]));
  }, [slug]);
  const download = async (d) => {
    try {
      const r = await axios.get(`${API}/api/v3/cabinet/documents/${d.document_id}/download`, { headers: hdr(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Téléchargement impossible"); }
  };
  return (
    <div style={T.card} data-testid="admin-documents">
      <h2 style={T.h2}>Documents reçus</h2>
      <div style={{ display: "grid", gap: 0, marginTop: 6 }}>
        {(docs || []).map((d) => (
          <div key={d.document_id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 0", borderBottom: "1px solid #F3F4F6" }} data-testid={`admin-doc-${d.document_id}`}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>📄</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.original_filename}</div>
              <div style={{ ...T.sub, fontSize: 11 }}>{d.client_name} · {fmtSize(d.size)} · {String(d.at).slice(0, 10)}</div>
            </div>
            <button style={{ background: "none", border: "none", color: "#0E8A4F", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }} onClick={() => download(d)} data-testid={`admin-doc-open-${d.document_id}`}>Ouvrir</button>
          </div>
        ))}
        {docs !== null && docs.length === 0 && <div style={{ ...T.sub, paddingTop: 8 }}>Aucun document reçu.</div>}
        {docs === null && <div style={{ ...T.sub, paddingTop: 8 }}>Chargement…</div>}
      </div>
    </div>
  );
};

// Rappel WhatsApp — MODE PROTOTYPE : wa.me pré-rempli (envoi en un tap), marqué envoyé.
const waLink = (phone, message) => {
  let p = String(phone || "").replace(/\D/g, "");
  if (p.length === 10 && p.startsWith("0")) p = `225${p.slice(1)}`;
  return `https://wa.me/${p}?text=${encodeURIComponent(message)}`;
};

export const AdminReminders = ({ slug }) => {
  const [reminders, setReminders] = useState([]);
  const load = () => axios.get(`${API}/api/v3/cabinet/${slug}/admin/reminders`, { headers: hdr() })
    .then((r) => setReminders(r.data.reminders)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!reminders.length) return null;
  const sendWa = async (r) => {
    const msg = r.visio_path ? `${r.message}${window.location.origin}${r.visio_path}` : r.message;
    window.open(waLink(r.client_phone, msg), "_blank");
    try {
      await axios.post(`${API}/api/v3/cabinet/${slug}/admin/reminders/${r.appointment_id}/sent`, {}, { headers: hdr() });
      toast.success("Rappel marqué comme envoyé ✓");
      load();
    } catch { /* non bloquant */ }
  };
  return (
    <div style={{ ...T.card, border: "1.5px solid rgba(249,115,22,0.45)", background: "#FFF9F3", marginBottom: 12 }} data-testid="admin-reminders">
      <h2 style={{ ...T.h2, color: "#C2410C" }}>⏰ Rappels à envoyer — RDV dans moins d'1 h</h2>
      <div style={{ display: "grid", gap: 0, marginTop: 4 }}>
        {reminders.map((r) => (
          <div key={r.appointment_id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 0", flexWrap: "wrap" }} data-testid={`reminder-${r.appointment_id}`}>
            <div style={{ fontSize: 13, fontWeight: 900, color: "#C2410C", width: 44 }}>{r.time}</div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800 }}>{r.client_name} · {r.mode === "visio" ? "📹 Visio" : "Au cabinet"}</div>
              <div style={{ ...T.sub, fontSize: 11 }}>avec {r.member_name}</div>
            </div>
            <button style={{ ...T.btnGreen, background: "#25D366", padding: "8px 13px", fontSize: 11.5 }} onClick={() => sendWa(r)} data-testid={`reminder-send-${r.appointment_id}`}>
              📲 Envoyer le rappel WhatsApp
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
